import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useUserStore } from '../stores/user-store'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

const routes: Array<RouteRecordRaw> = [
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'fields' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        // component: () => import('../pages/admin/dashboard/Dashboard.vue'),
        component: () => import('../pages/fields/FieldsPage.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
      },
      {
        name: 'plant-categories',
        path: 'plant-categories',
        component: () => import('../pages/plant-categories/PlantCategoriesPage.vue'),
      },
      {
        name: 'sensor-rules',
        path: 'sensor-rules',
        component: () => import('../pages/sensor-rules/SensorRulesPage.vue'),
      },
      {
        name: 'sensor-metadata',
        path: 'sensor-metadata',
        component: () => import('../pages/sensor-metadata/SensorMetadataPage.vue'),
      },
      {
        name: 'field-types',
        path: 'field-types',
        component: () => import('../pages/field-types/FieldTypesPage.vue'),
      },
      {
        name: 'device-types',
        path: 'device-types',
        component: () => import('../pages/device-types/DeviceTypesPage.vue'),
      },
      {
        name: 'fields',
        path: 'fields',
        component: () => import('../pages/fields/FieldsPage.vue'),
      },
      {
        name: 'field',
        path: 'field/:id',
        component: () => import('../pages/fields/FieldPage.vue'),
      },
      {
        name: 'diagnostic',
        path: 'diagnostic',
        component: () => import('../pages/diagnostic/DiagnosticPage.vue'),
      },
      {
        name: 'job-comments',
        path: 'field/:fieldId/job/:jobId/comments',
        component: () => import('../pages/fields/JobCommentsPage.vue'),
      },
      {
        name: 'devices',
        path: 'devices',
        component: () => import('../pages/devices/DevicesPage.vue'),
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/:pathMatch(.*)*',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

// Navigation guard to check authentication and authorization
router.beforeEach(async (to, from, next) => {
  const isAuthenticated = !!(localStorage.getItem('access_token') || sessionStorage.getItem('access_token'))
  const publicPages = ['login', 'signup', 'recover-password', 'recover-password-email', '404']
  const adminOnlyPages = ['users']

  if (!publicPages.includes(to.name as string) && !isAuthenticated) {
    next({ name: 'login' })
  } else if (adminOnlyPages.includes(to.name as string)) {
    // Check if user has admin role
    const userStore = useUserStore()

    // Fetch user profile if not already loaded
    if (!userStore.role) {
      try {
        await userStore.fetchUserProfile()
      } catch (error) {
        console.error('Failed to fetch user profile:', error)
        next({ name: 'fields' })
        return
      }
    }

    if (userStore.role !== 'admin') {
      // Redirect non-admin users to fields page
      next({ name: 'fields' })
    } else {
      next()
    }
  } else {
    next()
  }
})

export default router
